import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { usePortalConfig } from "@/portal/usePortalConfig";

interface Props {
  children: React.ReactElement;
  requireAdmin?: boolean;
  requirePermission?: string;
  /** Раздел инсталляции, без которого страницы здесь нет вовсе. По умолчанию
   *  берётся из `requirePermission` (у страниц модулей это одно и то же имя);
   *  задаётся явно для админских экранов настройки модуля. */
  requirePage?: string;
  /** Секция настроек (реестр `settings_sections.py`), без которой страницы
   *  настроек здесь нет. Своё измерение: секция «Печать» есть, пока есть хоть
   *  одно назначение печати, и ни одному разделу не равна. */
  requireSection?: string;
}

export function ProtectedRoute({
  children,
  requireAdmin,
  requirePermission,
  requirePage,
  requireSection,
}: Props): React.ReactElement {
  const { user, loading } = useAuth();
  const { hasPage, hasSection } = usePortalConfig();
  const location = useLocation();

  // Профиль инсталляции проверяем ПЕРВЫМ и до прав: у админа обе проверки прав
  // ниже проходят всегда, и без этого он попадал бы на страницу выключенного
  // модуля, роутера которого на бэкенде нет — экран пустых 404.
  const page = requirePage ?? requirePermission;
  if (page && !hasPage(page)) {
    return <MissingModulePage />;
  }

  // Секция настроек — такая же проверка «этого здесь нет вовсе» и тоже до прав.
  if (requireSection && !hasSection(requireSection)) {
    return <MissingModulePage />;
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Загрузка…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (requireAdmin && !user.is_admin) {
    return <ForbiddenPage />;
  }

  if (requirePermission && !user.is_admin && !user.permissions.includes(requirePermission)) {
    return <ForbiddenPage />;
  }

  return children;
}

function ForbiddenPage(): React.ReactElement {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2">
      <h1 className="text-2xl font-semibold">403 — Доступ запрещён</h1>
      <p className="text-muted-foreground">Нет прав на просмотр этого раздела.</p>
    </div>
  );
}

function MissingModulePage(): React.ReactElement {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 text-center">
      <h1 className="text-2xl font-semibold">Раздел не подключён</h1>
      <p className="max-w-md text-muted-foreground">
        В этой инсталляции портала такого модуля нет. Это не вопрос прав —
        раздел отсутствует целиком.
      </p>
    </div>
  );
}
